import { useDataChainId } from '@ui/hooks/datachain';
import { useTimer } from '@ui/lib/timer/timerCtx';
import { getTradingOneClickV2Facet } from '@ui/services/contract/contract-instances/getTradingOneClickV2Facet';
import { useCallback, useEffect } from 'react';
import { zeroAddress } from 'viem';
import { useAccount, usePublicClient } from 'wagmi';

import { getOneClickWalletRecord } from '../oneClickWalletHelper';
import { useOneClickTradingStore } from '../store/useOneClickTradingStore';

const AUTO_CHECK_INTERVAL_SECONDS = 10n;

let lastAutoCheckTimestamp: bigint | null = null;
let lastCheckedAddress: string | undefined;

export const useCheckOneClickTradingStatus = () => {
  const { address, isConnected } = useAccount();
  const chainId = useDataChainId();
  const publicClient = usePublicClient({ chainId });
  const { timestamp } = useTimer();

  const setStateFromWallet = useOneClickTradingStore(
    (s) => s.setStateFromWallet,
  );

  const update = useCallback(async () => {
    if (typeof window === 'undefined') return;
    if (!isConnected || !address) {
      // Keep previous state if wallet disconnects, similar to previous behavior.
      return;
    }

    const ownerKey = address.toLowerCase();

    try {
      // 1. Check local 1CT wallet record
      const record = await getOneClickWalletRecord(ownerKey);

      if (!record) {
        setStateFromWallet({
          created: false,
          enabled: false,
          hasCheckedStatus: true,
          owner: address,
          oneClickWalletAddress: undefined,
        });

        return;
      }

      // Always reflect latest record data
      const oneClickWalletAddress = record.oneClickWalletAddress as any;

      // 2. Check delegation status on-chain via TradingOneClickV2Facet
      let enabled = false;
      if (publicClient && chainId) {
        try {
          const contract = getTradingOneClickV2Facet({
            publicClient,
            chainId,
          });

          const result = (await contract.read.getAgentAuth([
            address,
            record.oneClickWalletAddress as any,
          ])) as readonly [string, string, bigint, number];

          // result[0] = agent address; if non-zero, delegation is active
          if (
            typeof result[0] === 'string' &&
            result[0].toLowerCase() !== zeroAddress.toLowerCase()
          ) {
            enabled = true;
          }
        } catch {
          // ignore on-chain errors; keep enabled as false
        }
      }

      setStateFromWallet({
        created: true,
        enabled,
        hasCheckedStatus: true,
        owner: address,
        oneClickWalletAddress,
      });
    } catch {
      // Ignore errors from IndexedDB or contract instances; state remains unchanged.
    }
  }, [address, chainId, isConnected, publicClient, setStateFromWallet]);

  // Auto-check periodically based on global timer, with a shared throttle.
  useEffect(() => {
    if (!isConnected || !address) return;

    const isSameAddress = lastCheckedAddress === address;

    if (lastAutoCheckTimestamp !== null && isSameAddress) {
      const diff = timestamp - lastAutoCheckTimestamp;
      if (diff < AUTO_CHECK_INTERVAL_SECONDS) {
        return;
      }
    }

    lastAutoCheckTimestamp = timestamp;
    lastCheckedAddress = address;
    void update();
  }, [timestamp, isConnected, address, update]);

  // Expose manual update so callers can trigger a fresh check (e.g. after tx).
  return { update };
};


